import { useMemo } from 'react';

import type { ToolsTableRow } from '../../types';

/**
 * Hook to compute registry chat statistics including enabled/disabled counts
 * and tool names for bulk operations. Only considers active (not disabled) tools.
 */
export function useRegistryChatStats(toolRow: ToolsTableRow) {
  return useMemo(() => {
    if (!toolRow.isRegistry) {
      return {
        allEnabled: false,
        allDisabled: false,
        enabledCount: 0,
        totalCount: 0,
        toolNames: [],
        allToolsDisabled: false,
      };
    }

    const tools = toolRow.items.filter(item => !item.isRegistry && 'tool' in item && item.tool);
    const activeTools = tools.filter(item => 'tool' in item && !item.tool.is_disabled);
    const chatEnabledTools = tools.filter(
      item => 'tool' in item && !item.tool.is_disabled && item.tool.is_enabled_in_primary_agent
    );
    const toolNames = activeTools
      .filter(item => 'tool' in item)
      .map(item => ('tool' in item ? item.tool.name : ''));

    return {
      allEnabled: chatEnabledTools.length === activeTools.length && activeTools.length > 0,
      allDisabled: chatEnabledTools.length === 0,
      enabledCount: chatEnabledTools.length,
      totalCount: activeTools.length,
      toolNames,
      allToolsDisabled: activeTools.length === 0,
    };
  }, [toolRow]);
}

/**
 * Hook to generate contextual tooltip content for registry chat toggle
 */
export function useRegistryChatTooltip(
  allToolsDisabled: boolean,
  totalCount: number,
  allEnabled: boolean,
  allDisabled: boolean,
  enabledCount: number
) {
  return useMemo(() => {
    if (allToolsDisabled) {
      return 'All tools in this registry are disabled. Enable tools first to add them to chat.';
    }
    if (totalCount === 0) {
      return 'No active tools in this registry';
    }
    if (allEnabled) {
      return 'Remove all tools from chat';
    }
    if (allDisabled) {
      return 'Add all tools to chat';
    }
    return `${enabledCount} of ${totalCount} tools enabled in chat. Click to remove all.`;
  }, [allToolsDisabled, totalCount, allEnabled, allDisabled, enabledCount]);
}
